import * as React from 'react';

import { requestModelDataForProject } from 'src/actions/projects/action-creators/requestModelDataForProject';
import { AddModelType } from 'src/containers/ProjectModelDialog/hooks/useAddModelTypeValue';
import { useImportModel } from 'src/containers/ProjectModelDialog/hooks/useImportModelValue';
import { useUploadModel } from 'src/containers/ProjectModelDialog/hooks/useUploadModelValue';
import { connectionSelector } from 'src/selectors/connection';
import { PendingModelData } from 'src/store/state/projects';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useRequestModelData = (type: AddModelType | null) => {
  const {
    databaseId: importDatabaseId,
    setIsModelDataLoading: setIsImportModelDataLoading,
  } = useImportModel();
  const {
    databaseId: uploadDatabaseId,
    setIsModelDataLoading: setIsUploadModelDataLoading,
  } = useUploadModel();
  const { projectId } = useRouterParams();
  const connection = useReduxSelector(connectionSelector);
  const dispatch = useReduxDispatch();

  const handleRequestModelData = React.useCallback(
    async (modelNames: string[]): Promise<Record<string, PendingModelData>> => {
      const isCorrectType =
        type === AddModelType.IMPORT || type === AddModelType.UPLOAD;
      const databaseId =
        type === AddModelType.IMPORT ? importDatabaseId : uploadDatabaseId;
      if (!connection || !isCorrectType || !databaseId || !modelNames.length) {
        return {};
      }

      const setIsModelDataLoading =
        type === AddModelType.IMPORT
          ? setIsImportModelDataLoading
          : setIsUploadModelDataLoading;

      setIsModelDataLoading(true);
      try {
        return await dispatch(
          requestModelDataForProject({
            projectId,
            databaseId,
            modelNames,
          })
        );
      } finally {
        setIsModelDataLoading(false);
      }
    },
    [
      connection,
      importDatabaseId,
      projectId,
      uploadDatabaseId,
      type,
      setIsImportModelDataLoading,
      setIsUploadModelDataLoading,
      dispatch,
    ]
  );

  return handleRequestModelData;
};
